// src/frontend/data/books/BlockchainFundamentals/data/cap1.js

const CAP1 = {
  id: 1,
  slug: "cap1-bitcoin",
  title: "Capítulo 1 — Bitcoin: fundamentos, consenso, halving, Layer 2 y evolución",
  sections: [
    {
      heading: "¿Qué es Bitcoin?",
      text: [
        "Bitcoin (BTC) es la primera criptomoneda y la primera blockchain pública en lograr adopción global.",
        "Es un sistema de dinero digital peer-to-peer (P2P) que permite transferir valor sin bancos ni intermediarios, usando reglas públicas, criptografía y consenso distribuido.",
        "Opera 24/7 y funciona como: protocolo de software, sistema de pago global y moneda digital descentralizada.",
      ],
    },
    {
      heading: "Orígenes: 2008–2009 y el nacimiento de una idea",
      text: [
        "La historia moderna de Bitcoin comienza en 2008, cuando se publica el documento técnico (whitepaper) que describe un “sistema de efectivo electrónico P2P”.",
        "En 2009 la red se pone en marcha y demuestra que se puede crear dinero digital sin una autoridad central.",
        "La propuesta buscaba resolver problemas del sistema tradicional: intermediación obligatoria, riesgo de censura, dependencia de políticas monetarias y confianza en terceros.",
      ],
    },
    {
      heading: "Satoshi Nakamoto y la filosofía de código abierto",
      text: [
        "El creador (o creadores) de Bitcoin, conocido como Satoshi Nakamoto, permanece en el anonimato.",
        "Bitcoin es software de código abierto: cualquiera puede auditarlo, verificar cómo funciona y proponer mejoras.",
        "Esa transparencia y auditoría pública fortalecen la seguridad y la confianza: la red no depende de ‘creerle’ a alguien, sino de revisar reglas y evidencia.",
      ],
    },
    {
      heading: "Bitcoin es más que una moneda",
      text: [
        "Bitcoin también es un protocolo: un conjunto de reglas que todos los nodos siguen para decidir qué transacciones son válidas.",
        "La confianza no se deposita en una empresa o gobierno, sino en matemáticas, software abierto e incentivos económicos.",
        "La idea clave es simple y poderosa: en Bitcoin, nadie “emite el dinero” por decisión política; la emisión está programada.",
      ],
    },
    {
      heading: "Blockchain en términos simples (bloques, hashes y cadena)",
      text: [
        "Las transacciones se agrupan en bloques. Cada bloque contiene un hash del bloque anterior, formando una cadena.",
        "Un hash funciona como una huella digital: si cambias cualquier dato, el hash cambia completamente.",
        "Gracias a esta estructura, modificar el pasado es extremadamente difícil y fácilmente detectable por la red.",
      ],
    },
    {
      heading: "Modelo UTXO: cómo ‘vive’ el dinero en Bitcoin",
      text: [
        "Bitcoin no maneja saldos como una cuenta bancaria tradicional.",
        "Utiliza el modelo UTXO (Unspent Transaction Outputs): cada transacción consume salidas anteriores y crea nuevas, similar a usar billetes y recibir cambio.",
        "Este modelo es distinto al de Ethereum, que usa cuentas con saldo (account-based).",
      ],
    },
    {
      heading: "¿Cómo funciona Bitcoin (paso a paso)?",
      list: [
        "Un usuario crea una transacción y la firma con su clave privada (prueba criptográfica de autorización).",
        "La transacción se transmite a la red y los nodos verifican que cumpla las reglas.",
        "Los mineros agrupan transacciones válidas en un bloque candidato.",
        "Los mineros compiten en Proof of Work (PoW) para encontrar un bloque válido.",
        "La red acepta el bloque y lo añade a la cadena. Cada bloque extra encima aumenta la seguridad (confirmaciones).",
      ],
    },
    {
      heading: "El consenso en Bitcoin",
      text: [
        "El consenso es el mecanismo que permite que miles de nodos estén de acuerdo sobre el estado de la red sin confiar entre sí.",
        "Bitcoin usa Proof of Work: la cadena válida es la que tiene mayor trabajo acumulado.",
        "El consenso no depende de votos humanos ni de una autoridad central.",
      ],
    },
    {
      heading: "Qué NO es el consenso",
      list: [
        "No es una votación política.",
        "No lo controla una empresa.",
        "No cambia por errores menores de software o por fallas de una sola app.",
        "No se modifica sin un acuerdo amplio de la red.",
      ],
    },
    {
      heading: "Minería: seguridad, verificación y emisión",
      text: [
        "La minería es el proceso mediante el cual se verifican transacciones y se produce Proof of Work.",
        "La competencia entre mineros hace caro atacar la red y ayuda a proteger el historial de transacciones.",
        "Durante el proceso se crea nueva moneda (subsidio por bloque) y se cobran comisiones.",
      ],
    },
    {
      heading: "Confirmaciones y finalidad práctica",
      text: [
        "Cuando una transacción entra en un bloque, recibe una confirmación.",
        "Cada bloque adicional reduce el riesgo de reversión.",
        "En Bitcoin la finalidad es probabilística: mientras más confirmaciones, más seguridad.",
      ],
    },
    {
      heading: "Oferta total: los 21 millones (escasez programada)",
      text: [
        "Bitcoin tiene una política monetaria fija: solo existirán 21 millones de BTC.",
        "Esto lo diferencia del dinero fiduciario, donde la oferta puede expandirse por decisiones externas.",
        "Además, Bitcoin es divisible en unidades muy pequeñas: 1 BTC puede dividirse en 100,000,000 unidades (satoshis).",
      ],
    },
    {
      heading: "Halving: qué es y por qué importa",
      text: [
        "El halving ocurre aproximadamente cada 4 años (210,000 bloques).",
        "Reduce a la mitad la recompensa que reciben los mineros por bloque.",
        "Esto disminuye la nueva oferta de BTC y afecta la economía de la minería.",
        "No garantiza subidas de precio, pero sí influye en expectativas y narrativa del mercado.",
      ],
    },
    {
      heading: "¿Cómo se determina el precio de Bitcoin?",
      text: [
        "El precio de Bitcoin se determina principalmente por oferta y demanda en los mercados.",
        "Factores comunes: adopción, liquidez, confianza, regulación, ciclos macroeconómicos, narrativa y percepción de escasez.",
        "La volatilidad es una consecuencia natural de un activo global con emisión limitada y demanda que cambia rápidamente.",
      ],
    },
    {
      heading: "Wallets, llaves y propiedad real",
      text: [
        "Los bitcoins no son ‘archivos’ guardados en tu PC. Lo que tienes es control criptográfico: una clave privada que puede autorizar gastos desde ciertas direcciones.",
        "Tu wallet gestiona llaves y te permite firmar transacciones.",
        "Idea clave: si no controlas tus llaves privadas, no controlas realmente tus fondos.",
      ],
    },
    {
      heading: "Conceptos básicos (para no confundirte)",
      list: [
        "Bitcoin (red): se refiere al sistema y la red de transacciones.",
        "BTC (unidad): es la unidad de cuenta y el activo intercambiable.",
        "Blockchain: libro contable público/distribuido donde se registran transacciones.",
        "Minería: verificación + seguridad + emisión programada mediante Proof of Work.",
      ],
    },
    {
      heading: "Escalabilidad y Layer 2",
      text: [
        "Bitcoin prioriza seguridad y descentralización, lo que limita el número de transacciones en la capa base.",
        "Para escalar, se usan soluciones de segunda capa (Layer 2) que descargan trabajo de la blockchain principal.",
      ],
    },
    {
      heading: "Lightning Network (Layer 2 más conocida)",
      list: [
        "Permite pagos rápidos y baratos mediante canales de pago.",
        "Las transacciones ocurren fuera de la cadena principal y solo se liquidan al abrir/cerrar el canal.",
        "Hace posibles micropagos y uso cotidiano con menos tarifas.",
        "Como toda infraestructura, requiere buenas prácticas: wallets confiables, liquidez y evitar enlaces sospechosos.",
      ],
    },
    {
      heading: "Forks en Bitcoin: qué son y por qué ocurren",
      text: [
        "Un fork es una bifurcación en reglas o en la historia de la cadena.",
        "Puede ocurrir por actualizaciones técnicas o por desacuerdos sobre el rumbo del protocolo.",
        "En general, Bitcoin cambia con mucha cautela: la compatibilidad y la seguridad se consideran prioridad.",
      ],
    },
    {
      heading: "Soft fork vs hard fork (con ejemplos)",
      list: [
        "Soft fork: cambio compatible hacia atrás (ejemplos conocidos: SegWit, Taproot).",
        "Hard fork: cambio no compatible; si la comunidad se divide, nacen redes distintas (ejemplos: Bitcoin Cash, Bitcoin SV).",
      ],
    },
    {
      heading: "El timestamp en Bitcoin: límite técnico, no crisis",
      text: [
        "Cada bloque incluye un campo de tiempo (timestamp) que indica cuándo fue minado aproximadamente.",
        "Ese tiempo usa un formato numérico con rango finito; a muy largo plazo (décadas) puede requerir ajustes.",
        "Esto no afecta directamente al consenso (Proof of Work) ni implica por sí mismo que ‘deba haber’ un hard fork.",
        "Cuando llegue el momento, puede resolverse con una actualización cuidadosamente diseñada y acordada por la red.",
      ],
    },
    {
      heading: "Bitcoin como referente global y adopción hacia 2025",
      text: [
        "Bitcoin se consolidó como referencia del ecosistema: muchas altcoins se miden contra BTC como activo base de comparación.",
        "En años recientes se incrementó la presencia institucional y la integración con productos financieros en distintos mercados.",
        "También creció la adopción comercial: desde la famosa anécdota de 2010 (dos pizzas pagadas con 10,000 BTC) hasta comercios y servicios que hoy aceptan BTC directa o indirectamente.",
        "El debate sobre escalabilidad sigue: por eso soluciones como Lightning son relevantes para pagos cotidianos.",
      ],
    },
    {
      heading: "Criptomonedas y proyectos relacionados (ejemplos)",
      list: [
        "Zcash (ZEC): sistema financiero abierto con enfoque en privacidad.",
        "Dash (DASH): dinero digital descentralizado orientado a pagos rápidos.",
        "Dogecoin (DOGE): criptomoneda P2P open source con fuerte componente social.",
        "Monero (XMR): moneda privada y segura de código abierto.",
        "Ripple (XRP): red para pagos internacionales rápidos y de bajo costo.",
        "SolarCoin (SLR): proyecto que incentiva la generación de energía solar.",
        "Ethereum (ETH): plataforma descentralizada para contratos inteligentes.",
      ],
    },
    {
      heading: "Buenas prácticas al usar Bitcoin",
      list: [
        "Nunca compartas tu frase semilla (seed phrase). Nadie legítimo te la pide.",
        "Haz pruebas con montos pequeños antes de mover cantidades grandes.",
        "Verifica direcciones (copiar/pegar + revisar primeros/últimos caracteres).",
        "Desconfía de enlaces, airdrops y ‘soporte’ por mensajes privados.",
        "Para montos grandes, considera hardware wallet y copias de seguridad fuera de línea.",
      ],
    },
    {
      heading: "FAQ rápida (para el curso)",
      list: [
        "¿Qué es Bitcoin y por qué es distinto al dinero tradicional? → Porque no lo emite una autoridad central y su oferta está programada.",
        "¿Cómo se crean nuevos bitcoins? → Por minería (subsidio por bloque) y se reduce con halvings.",
        "¿Qué es blockchain en relación a Bitcoin? → Es el libro contable distribuido donde se registran transacciones.",
        "¿Por qué es volátil? → Oferta limitada + demanda variable + narrativa + eventos macro/regulatorios.",
        "¿Puedo usarlo para comprar cosas? → Sí, directamente o mediante servicios; la experiencia depende de comisiones y de Layer 2.",
      ],
    },


    /* =========================================================
       NUEVOS TEMAS AGREGADOS (sin borrar lo anterior)
       ========================================================= */

    {
      heading: "¿Quién tiene más Bitcoin? (holdings corporativos e institucionales)",
      meta: {
        sourceNote:
          "Resumen educativo basado en texto proporcionado por ti (referencia: abril 2025). Las cifras pueden cambiar con el tiempo.",
        date: "2025-04-25",
        level: "Básico",
        readTime: "6 min",
      },
      text: [
        "Una parte importante del mercado de Bitcoin se ve influenciada por grandes tenedores (holders), especialmente empresas públicas, mineras, custodios y firmas de inversión.",
        "Monitorear holdings ayuda a entender narrativas de oferta/demanda, presión de venta (mineros) y adopción institucional (tesorerías corporativas).",
      ],
      list: [
        "Strategy (antes MicroStrategy): 538,200 BTC (mencionado como el mayor acumulador corporativo).",
        "Marathon Digital Holdings: 47,531 BTC.",
        "Riot Platforms: 19,223 BTC.",
        "CleanSpark: 11,869 BTC.",
        "Tesla: 11,509 BTC.",
        "Hut 8 Corp: 10,264 BTC.",
        "Coinbase Global: 9,000 BTC.",
        "Block, Inc. (antes Square): 8,485 BTC.",
        "Galaxy Digital Holdings: 8,100 BTC.",
        "Metaplanet Inc.: 4,855 BTC.",
        "Bitcoin Group SE: 3,830 BTC.",
      ],
    },

    {
      heading: "¿Cuántos Bitcoins quedan por minar en 2025?",
      meta: {
        sourceNote:
          "Resumen educativo basado en texto proporcionado por ti (referencia: diciembre 2025).",
        date: "2025-12-03",
        level: "Básico",
        readTime: "9 min",
      },
      text: [
        "En 2025 se menciona que quedan aproximadamente 1.2 millones de BTC por minar.",
        "Se estima que más de 19.8 millones (aprox. 94% del total) ya fueron minados.",
        "Parte del suministro “realmente disponible” sería menor por pérdidas de claves/semillas (se menciona un rango de 3 a 4 millones de BTC perdidos).",
        "La emisión se extiende gradualmente hasta alrededor del año 2140 debido a la naturaleza exponencial de los halvings.",
      ],
    },

    {
      heading: "¿Cuántos BTC se minan al día en 2025? (regla de los ~10 minutos)",
      text: [
        "Tras el halving de abril de 2024, la recompensa por bloque pasó de 6.25 BTC a 3.125 BTC.",
        "Si el promedio es ~1 bloque cada 10 minutos, se producen ~144 bloques al día.",
      ],
      list: [
        "144 bloques/día × 3.125 BTC/bloque ≈ 450 BTC/día",
        "Esta cifra reduce la “nueva oferta diaria” y suele reforzar la narrativa de escasez.",
      ],
    },

    {
      heading: "Tabla de Halvings de Bitcoin (2009 – ~2140)",
      text: [
        "Los halvings ocurren cada 210,000 bloques (aprox. cada 4 años) y reducen la recompensa por bloque.",
        "Esta tabla es útil para visualizar la política monetaria programada de Bitcoin (cómo se va “secando” la emisión).",
      ],
      table: {
        columns: ["Evento", "Año Estimado", "Bloque #", "Recompensa (BTC)", "Estado"],
        rows: [
          ["Lanzamiento (Génesis)", "2009", "0", "50.00000000", "Pasado"],
          ["1.er Halving", "2012", "210,000", "25.00000000", "Pasado"],
          ["2.º Halving", "2016", "420,000", "12.50000000", "Pasado"],
          ["3.er Halving", "2020", "630,000", "6.25000000", "Pasado"],
          ["4.º Halving", "2024", "840,000", "3.12500000", "Actual"],
          ["5.º Halving", "2028", "1,050,000", "1.56250000", "Futuro"],
          ["6.º Halving", "2032", "1,260,000", "0.78125000", "Futuro"],
          ["7.º Halving", "2036", "1,470,000", "0.39062500", "Futuro"],
          ["8.º Halving", "2040", "1,680,000", "0.19531250", "Futuro"],
          ["9.º Halving", "2044", "1,890,000", "0.09765625", "Futuro"],
          ["10.º Halving", "2048", "2,100,000", "0.04882812", "Futuro"],
          ["11.º Halving", "2052", "2,310,000", "0.02441406", "Futuro"],
          ["12.º Halving", "2056", "2,520,000", "0.01220703", "Futuro"],
          ["13.º Halving", "2060", "2,730,000", "0.00610351", "Futuro"],
          ["14.º Halving", "2064", "2,940,000", "0.00305175", "Futuro"],
          ["15.º Halving", "2068", "3,150,000", "0.00152587", "Futuro"],
          ["16.º Halving", "2072", "3,360,000", "0.00076293", "Futuro"],
          ["17.º Halving", "2076", "3,570,000", "0.00038146", "Futuro"],
          ["18.º Halving", "2080", "3,780,000", "0.00019073", "Futuro"],
          ["19.º Halving", "2084", "3,990,000", "0.00009536", "Futuro"],
          ["20.º Halving", "2088", "4,200,000", "0.00004768", "Futuro"],
          ["21.º Halving", "2092", "4,410,000", "0.00002384", "Futuro"],
          ["22.º Halving", "2096", "4,620,000", "0.00001192", "Futuro"],
          ["23.º Halving", "2100", "4,830,000", "0.00000596", "Futuro"],
          ["24.º Halving", "2104", "5,040,000", "0.00000298", "Futuro"],
          ["25.º Halving", "2108", "5,250,000", "0.00000149", "Futuro"],
          ["26.º Halving", "2112", "5,460,000", "0.00000074", "Futuro"],
          ["27.º Halving", "2116", "5,670,000", "0.00000037", "Futuro"],
          ["28.º Halving", "2120", "5,880,000", "0.00000018", "Futuro"],
          ["29.º Halving", "2124", "6,090,000", "0.00000009", "Futuro"],
          ["30.º Halving", "2128", "6,300,000", "0.00000004", "Futuro"],
          ["31.º Halving", "2132", "6,510,000", "0.00000002", "Futuro"],
          ["32.º Halving", "2136", "6,720,000", "0.00000001 (1 Sat)", "Futuro"],
          ["33.º Halving (Final)", "~2140", "6,930,000", "0.00000000", "Fin de emisión"],
        ],
         image: {
            src: "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/DarckForest/opensea_3rd.png",
            alt: "Ejemplo de un script JavaScript remoto referenciado desde el frontend de una DApp.",
            caption:
              "Ejemplo ilustrativo de un archivo JavaScript remoto cargado por una DApp. Si el archivo remoto se ve comprometido, todos los usuarios pueden quedar expuestos."
          }
      },
      images: [
        {
          // 👇 PÉGAME AQUÍ EL LINK CUANDO ME LO DES
          src: "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/cryptomoendasBook/ChatGPT%20Image%206%20ene%202026%2C%2003_54_13%20a.m..png",
          alt: "Gráfico/imagen relacionada a la tabla de halvings de Bitcoin",
          caption: "Cronograma de halvings y reducción programada de la emisión (imagen por agregar).",
        },
      ],
    },

    {
      heading: "Computación cuántica y Bitcoin: ¿amenaza real o riesgo a largo plazo?",
      meta: {
        sourceNote:
          "Resumen educativo basado en texto proporcionado por ti (referencia: diciembre 2024).",
        date: "2024-12-20",
        level: "Básico",
        readTime: "14 min",
      },
      text: [
        "Bitcoin se apoya en criptografía para proteger transacciones y propiedad: hashing (SHA-256) y firmas digitales (ECDSA).",
        "La computación cuántica podría cambiar el “costo” de romper ciertos problemas matemáticos, lo que abre riesgos teóricos para sistemas criptográficos actuales.",
      ],
      list: [
        "ECDSA: se usa para firmas; con computación cuántica avanzada, ciertos escenarios podrían volverse más vulnerables (idea general: derivar claves privadas desde claves públicas).",
        "SHA-256: también puede verse afectado por algoritmos cuánticos, pero se describe como un reto más exigente que atacar ECDSA.",
        "Mitigación: la red puede evolucionar a esquemas post-cuánticos cuando sea necesario (actualizaciones del protocolo, nuevas firmas, etc.).",
      ],
    },

    {
      heading: "Perspectivas para Bitcoin hacia 2025: adopción, ETFs y narrativa de mercado",
      meta: {
        sourceNote:
          "Resumen educativo basado en texto proporcionado por ti (referencia: diciembre 2024).",
        date: "2024-12-30",
        level: "Básico",
        readTime: "10 min",
      },
      text: [
        "Se describe 2024 como un año clave por mayor integración con finanzas tradicionales (por ejemplo, productos tipo ETF al contado) y crecimiento de interés institucional.",
        "Para 2025 se proyectan escenarios donde la demanda institucional, la escasez reforzada por el halving y mejoras tecnológicas (Lightning/Taproot) influyen en expectativas del mercado.",
        "Aun con narrativas alcistas, se enfatiza que Bitcoin sigue siendo volátil y sensible a regulación, ciclos macro y cambios de liquidez.",
      ],
      list: [
        "Factores que suelen impulsar expectativas: demanda (instituciones), reducción de oferta nueva (halving), mejoras técnicas (L2), y claridad regulatoria.",
        "Riesgos: correcciones, incertidumbre regulatoria, shocks macroeconómicos, y cambios en sentimiento del mercado.",
      ],
    },
  ],
};

export default CAP1;
